import { Alert, Button, Card, DatePicker, Form, Input, Switch, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  title: string;
  started_on: dayjs.Dayjs;
  session_id?: string;
  framework_version_id?: string;
  summary?: string;
  seed_defaults?: boolean;
};

export default function EqamsFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.eqams.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ id: string }>('/education/eqams/cycles', {
        title: values.title,
        started_on: values.started_on.format('YYYY-MM-DD'),
        session_id: values.session_id || null,
        framework_version_id: values.framework_version_id || null,
        summary: values.summary,
        seed_defaults: values.seed_defaults !== false,
      }),
    onSuccess: (res) => {
      message.success(t('eqamsCycleCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/eqams/${id}` : '/education/eqams');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newEqamsCycle')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newEqamsCycle')}
      description={t('eqamsIntro')}
      extra={<Button onClick={() => navigate('/education/eqams')}>{t('eqams')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('eqamsReadinessHint')} />
        <Form
          layout="vertical"
          initialValues={{ seed_defaults: true, started_on: dayjs() }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="title" label={t('title')} rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="started_on" label={t('startedOn')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="session_id" label={t('sessionOptional')}>
            <EducationSessionSelect allowClear />
          </Form.Item>
          <Form.Item name="framework_version_id" label={t('frameworkVersionOptional')} extra={t('eqamsFrameworkHint')}>
            <Input placeholder={t('frameworkVersionIdPlaceholder')} maxLength={36} allowClear />
          </Form.Item>
          <Form.Item name="summary" label={t('summary')}>
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
          <Form.Item name="seed_defaults" label={t('seedDefaultDomains')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
